/**
 * datetime.js — single source of truth for displaying backend timestamps.
 *
 * The backend runs Django with USE_TZ=True and stores timestamps in UTC.
 * Depending on the endpoint, a timestamp reaches the frontend as:
 *   - UTC ISO with 'Z' or '+00:00'      (DRF serializers)
 *   - an offset-aware string '+05:30'   (views that use str(datetime))
 *   - a naive string (no offset)        (legacy views) — assumed UTC
 *
 * These helpers normalize all of the above to the USER'S LOCAL TIMEZONE so
 * times render correctly regardless of where the user is located.
 */

import moment from 'moment-timezone';

// Automatically detect the user's timezone.
export const APP_TIMEZONE = moment.tz.guess();

const PLACEHOLDER = '—';

/**
 * Parse any backend timestamp into a moment in the user's local timezone.
 * A string with NO timezone info is treated as UTC,
 * then converted to the user's timezone.
 * Returns null for empty/invalid input.
 */
export const toAppMoment = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  // Already a Date or epoch.
  if (value instanceof Date || typeof value === 'number') {
    const m = moment(value);
    return m.isValid() ? m.tz(APP_TIMEZONE) : null;
  }

  const str = String(value).trim();

  // Detect timezone suffix:
  // Z
  // +05:30
  // +0530
  // -04:00
  const hasTz = /(?:Z|[+-]\d{2}:?\d{2})$/.test(str);

  let m;

  if (hasTz) {
    // Respect timezone already present.
    m = moment.parseZone(str);
  } else {
    // No timezone present → assume UTC.
    m = moment.utc(
      str,
      [
        'YYYY-MM-DD HH:mm:ss.SSSSSS',
        'YYYY-MM-DD HH:mm:ss',
        'YYYY-MM-DDTHH:mm:ss.SSSSSS',
        'YYYY-MM-DDTHH:mm:ss',
        'YYYY-MM-DD HH:mm',
        moment.ISO_8601,
      ],
      true
    );
  }

  return m.isValid() ? m.tz(APP_TIMEZONE) : null;
};

/**
 * Format a timestamp for display.
 * Default: "20 May 2026, 23:14"
 */
export const formatDateTime = (value, fmt = 'DD MMM YYYY, HH:mm', fallback = PLACEHOLDER) => {
  const m = toAppMoment(value);
  return m ? m.format(fmt) : fallback;
};

/** Date only — "20 May 2026". */
export const formatDate = (value, fallback = PLACEHOLDER) =>
  formatDateTime(value, 'DD MMM YYYY', fallback);

/** Time only — "23:14". */
export const formatTime = (value, fallback = PLACEHOLDER) =>
  formatDateTime(value, 'HH:mm', fallback);

/** With seconds — "20 May 2026, 23:14:54". */
export const formatDateTimeSeconds = (value, fallback = PLACEHOLDER) =>
  formatDateTime(value, 'DD MMM YYYY, HH:mm:ss', fallback);

/** Relative — "3 hours ago", "in 2 days". */
export const formatRelative = (value, fallback = PLACEHOLDER) => {
  const m = toAppMoment(value);
  return m ? m.fromNow() : fallback;
};

/**
 * Numeric comparator for sorting AntD table columns by timestamp.
 */
export const sortTime = (value) => {
  const m = toAppMoment(value);
  return m ? m.valueOf() : 0;
};

export default formatDateTime;
